import React from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  FileText, 
  User, 
  Building2, 
  Wrench, 
  Calendar, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Edit2,
  MapPin,
  PackageCheck,
  ShieldCheck,
  XCircle
} from 'lucide-react';
import ImagenesPresupuesto from './ImagenesPresupuesto';
import { buildDisplayNameFromMaybeObject } from '../../lib/displayName';

export default function PresupuestoDetalleModal({ presupuesto, onClose, onEditar }) {
  if (!presupuesto) return null;

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    try {
      return new Date(fecha).toLocaleDateString('es-PY', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      borrador:             { color: 'bg-gray-100 text-gray-700 border border-gray-200',   icon: FileText,     label: 'Borrador' },
      verificado:           { color: 'bg-emerald-100 text-emerald-700 border border-emerald-200',   icon: ShieldCheck,  label: 'Verificado' },
      pendiente_aprobacion: { color: 'bg-yellow-100 text-yellow-700 border border-yellow-200', icon: Clock,   label: 'Pendiente Aprobación' },
      aprobado:             { color: 'bg-green-100 text-green-700 border border-green-200', icon: CheckCircle, label: 'Aprobado' },
      rechazado:            { color: 'bg-red-100 text-red-700 border border-red-200',       icon: XCircle,     label: 'Rechazado' },
      entregado:            { color: 'bg-purple-100 text-purple-700 border border-purple-200', icon: PackageCheck, label: 'Entregado' },
    };
    
    const config = estados[estado] || { color: 'bg-gray-100 text-gray-700', icon: AlertCircle, label: estado };
    const Icon = config.icon;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  // Línea de progreso del estado
  const FLUJO_ESTADOS = ['borrador', 'verificado', 'pendiente_aprobacion', 'aprobado', 'entregado'];
  const FLUJO_LABELS  = ['Borrador', 'Verificado', 'Pendiente', 'Aprobado', 'Entregado'];
  const estadoActualIdx = presupuesto.estado === 'rechazado' ? -1 : FLUJO_ESTADOS.indexOf(presupuesto.estado);

  const resolveTecnicoNombre = () => {
    return buildDisplayNameFromMaybeObject(
      presupuesto.tecnico_nombre || 
      presupuesto.nombre_tecnico || 
      presupuesto.tecnico || 
      presupuesto.usuario_creador
    ) || 'Sin asignar';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-1">Detalle de Presupuesto</h2>
              <div className="flex items-center gap-3 text-sm">
                {getEstadoBadge(presupuesto.estado)}
                <span className="text-emerald-100 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatearFecha(presupuesto.created_at)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {onEditar && (
              <button
                onClick={() => onEditar(presupuesto)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 backdrop-blur-sm border border-white/20"
              >
                <Edit2 className="w-4 h-4" />
                Editar
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Columna Izquierda: Info Principal */}
            <div className="lg:col-span-2 space-y-6">

              {/* Progreso del flujo de estados */}
              {presupuesto.estado === 'rechazado' ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <XCircle className="w-5 h-5 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-700">Presupuesto Rechazado</p>
                    {presupuesto.notas_cambio_estado && (
                      <p className="text-xs text-red-600 mt-0.5">{presupuesto.notas_cambio_estado}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Progreso</p>
                  <div className="flex items-center gap-1">
                    {FLUJO_ESTADOS.map((e, idx) => {
                      const done   = idx <= estadoActualIdx;
                      const active = idx === estadoActualIdx;
                      return (
                        <React.Fragment key={e}>
                          <div className={`flex flex-col items-center ${idx === 0 ? '' : 'flex-1'}`}>
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                              active ? 'bg-emerald-600 text-white shadow-md ring-2 ring-emerald-300' :
                              done   ? 'bg-green-500 text-white' :
                                       'bg-gray-100 text-gray-400'
                            }`}>
                              {done && !active ? '✓' : idx + 1}
                            </div>
                            <span className={`text-xs mt-1 font-medium text-center leading-tight ${
                              active ? 'text-emerald-600' : done ? 'text-green-600' : 'text-gray-400'
                            }`} style={{fontSize: '0.65rem'}}>
                              {FLUJO_LABELS[idx]}
                            </span>
                          </div>
                          {idx < FLUJO_ESTADOS.length - 1 && (
                            <div className={`flex-1 h-0.5 mb-4 rounded-full ${idx < estadoActualIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Descripción del Trabajo
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resumen</label>
                    <p className="text-gray-900 font-medium mt-1 text-lg">{presupuesto.descripcion || 'Sin descripción'}</p>
                  </div>
                  {presupuesto.detalle && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Detalle Técnico</label>
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{presupuesto.detalle}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Imágenes */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Documentación Visual
                </h3>
                <ImagenesPresupuesto 
                  presupuestoId={presupuesto._id}
                  imagenes={presupuesto.imagenes || []}
                  onImagenesChange={() => {}} // Read-only
                  puedeEditar={false}
                />
              </div>

            </div>

            {/* Columna Derecha: Metadatos y Relaciones */}
            <div className="space-y-6">
              
              {/* Cliente */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  Cliente
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-500">Nombre / Razón Social</div>
                    <div className="font-medium text-gray-900">{presupuesto.cliente_nombre || 'No especificado'}</div>
                  </div>
                  {presupuesto.sucursal_nombre && (
                    <div className="flex items-start gap-2 pt-2 border-t border-gray-50">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Sucursal</div>
                        <div className="font-medium text-gray-900">{presupuesto.sucursal_nombre}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Equipo */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <Wrench className="w-4 h-4 text-gray-500" />
                  Equipo
                </h3>
                {presupuesto.equipo_nombre ? (
                  <div className="space-y-2">
                    <div className="font-medium text-gray-900">{presupuesto.equipo_nombre}</div>
                    {presupuesto.equipo_id && (
                      <div className="text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
                        ID: {presupuesto.equipo_id.slice(-6)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">Sin equipo asignado</div>
                )}
              </div>

              {/* Técnico */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2 uppercase tracking-wider">
                  <User className="w-4 h-4 text-gray-500" />
                  Responsable
                </h3>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                    {resolveTecnicoNombre().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{resolveTecnicoNombre()}</div>
                    <div className="text-xs text-gray-500">Técnico Asignado</div>
                  </div>
                </div>
              </div>

              {/* Notas Internas (Solo Admin) */}
              {presupuesto.notas_internas && (
                <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
                  <h3 className="text-sm font-bold text-yellow-800 mb-2 flex items-center gap-2 uppercase tracking-wider">
                    <AlertCircle className="w-4 h-4" />
                    Notas Internas
                  </h3>
                  <p className="text-sm text-yellow-900 italic">
                    {presupuesto.notas_internas}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-white px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium border border-gray-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}